import React, { useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import DefaultFormGroup from '../../utils/forms/DefaultFormGroup';
import ButtonDutyFunctionSelect from '../general/ButtonDutyFunctionSelect';
import GenericGroupSelector from '../general/GroupSelector';
import GenericAvailabilityCodeSelector from '../general/AvailabilityCodeSelector';
import GenericPeriodSelector from '../../utils/forms/PeriodSelector';
import ToggleButtonSelect from '../general/ToggleButtonSelect';
import OverlappingDutyTransactions from './OverlappingDutyTransactions';

import {
  operations as standbyDutyFunctionsOperations,
  selectors as standbyDutyFunctionsSelectors
} from '../../state/modules/standby-duty-functions';
import {
  operations as availabilityCodesOperations,
  selectors as availabilityCodesSelector
} from '../../state/modules/availability-codes';
import { operations as groupOperations, selectors as groupSelectors } from '../../state/modules/groups';

const mapStateToPropsDutyFunctions = state => ({
  dutyFunctions: standbyDutyFunctionsSelectors.values(state),
  dutyFunctionsLoaded: standbyDutyFunctionsSelectors.initialLoadComplete(state)
});
const mapDispatchToPropsDutyFunctions = {
  fetchDutyFunctions: standbyDutyFunctionsOperations.fetch
};
const DutyFunctionSelector = connect(
  mapStateToPropsDutyFunctions,
  mapDispatchToPropsDutyFunctions
)(({ dutyFunctions, dutyFunctionsLoaded, fetchDutyFunctions, onChange, selected, groupId }) => {
  useEffect(() => {
    fetchDutyFunctions();
  }, [fetchDutyFunctions]);

  // Only re-calculate when the dutyFunctions change
  const activeDutyFunctions = useMemo(
    () => dutyFunctions.filter(dutyFunction => dutyFunction.active && dutyFunction.group_ids.includes(groupId)),
    [dutyFunctions, groupId]
  );

  const selectedFunctionIds = selected || [];

  return (
    <DefaultFormGroup
      className="dutyFunctionSelector"
      label={I18n.t('activerecord.models.standby_duty_function')}
      required={true}
    >
      <ButtonDutyFunctionSelect
        dutyFunctions={activeDutyFunctions}
        loading={!dutyFunctionsLoaded}
        selectedDutyFunctionIds={selectedFunctionIds}
        onClick={onChange}
      />
    </DefaultFormGroup>
  );
});

const PeriodSelector = ({ startTime, endTime, onChange }) => (
  <DefaultFormGroup className="periodSelector" label={I18n.t('period')}>
    <GenericPeriodSelector startTime={startTime} endTime={endTime} onChange={onChange} />
  </DefaultFormGroup>
);

const mapStateToPropsGroups = state => ({
  groups: groupSelectors.values(state)
});
const mapDispatchToPropsGroups = {
  fetchGroupAndSubgroups: groupOperations.fetchGroupAndSubgroups
};
const GroupSelector = connect(
  mapStateToPropsGroups,
  mapDispatchToPropsGroups
)(({ groupId, groups, onChange, fetchGroupAndSubgroups, value }) => {
  useEffect(() => {
    fetchGroupAndSubgroups(groupId);
  }, [fetchGroupAndSubgroups, groupId]);

  return (
    <DefaultFormGroup
      className="groupSelector"
      label={I18n.t('activerecord.attributes.schedule/exchanges/duty_transaction.receiving_group')}
    >
      <GenericGroupSelector value={value} groups={groups} onChange={onChange} />
    </DefaultFormGroup>
  );
});

const mapStateToPropsAvailabilityCodes = state => ({
  availabilityCodes: availabilityCodesSelector.selectForDutyRequest(state),
  availabilityCodesLoaded: availabilityCodesSelector.initialLoadComplete(state)
});
const mapDispatchToPropsAvailabilityCodes = {
  fetchAvailabilityCodes: availabilityCodesOperations.fetch
};
const AvailabilityCodeSelector = connect(
  mapStateToPropsAvailabilityCodes,
  mapDispatchToPropsAvailabilityCodes
)(({ availabilityCodes, availabilityCodesLoaded, fetchAvailabilityCodes, onChange, groupId }) => {
  useEffect(() => {
    fetchAvailabilityCodes();
  }, [fetchAvailabilityCodes]);

  const applicableAvailabilityCodes = useMemo(
    () => availabilityCodes.filter(availabilityCode => availabilityCode.applicable_to_groups.includes(groupId)),
    [availabilityCodes, groupId]
  );

  return (
    <DefaultFormGroup
      className="availabilityCodeSelector"
      label={I18n.t('activerecord.models.schedule/availability_code.one')}
    >
      <GenericAvailabilityCodeSelector
        loading={!availabilityCodesLoaded}
        availabilityCodes={applicableAvailabilityCodes}
        onChange={onChange}
        multi={false}
      />
    </DefaultFormGroup>
  );
});

const CommentsField = ({ value, onChange }) => (
  <DefaultFormGroup label={I18n.t('comments')}>
    <textarea value={value} className="form-control" onChange={onChange} rows="4" />
  </DefaultFormGroup>
);

const ResolutionMechanismSelector = ({ onChange, value }) => {
  const resolutionMechanisms = ['first_to_respond', 'manual'].map(method => ({
    value: method,
    label: I18n.t(`classy_enum.schedule/exchanges/duty_request_resolution_mechanism.${method}`)
  }));

  const values = [value];

  return (
    <DefaultFormGroup
      className="resolutionMechanismSelector"
      label={I18n.t('activerecord.attributes.schedule/exchanges/duty_transaction.resolution_mechanism')}
    >
      <ToggleButtonSelect options={resolutionMechanisms} values={values} onChange={onChange} />
    </DefaultFormGroup>
  );
};

const mapStateToPropsOverlappingRequests = state => ({
  groups: groupSelectors.values(state)
});

const OverlappingRequestsTable = connect(mapStateToPropsOverlappingRequests)(({ groups, period, className }) => {
  const groupIds = useMemo(() => groups.map(g => g.id), [groups]);

  const { startTime, endTime, initialEndTime } = period;

  return (
    <DefaultFormGroup
      className={className}
      label={I18n.t('activerecord.attributes.schedule/exchanges/duty_request.overlapping_duty_requests')}
    >
      <OverlappingDutyTransactions
        startTime={startTime}
        endTime={endTime || initialEndTime}
        onlyOfType={['duty_request']}
        groupIds={groupIds}
      />
    </DefaultFormGroup>
  );
});

export default {
  DutyFunctionSelector,
  PeriodSelector,
  GroupSelector,
  AvailabilityCodeSelector,
  ResolutionMechanismSelector,
  CommentsField,
  OverlappingRequestsTable
};
